import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, Observable, switchMap, take } from 'rxjs';
import { Account } from '../../../models/ui/account.model';
import { CaseAccess } from '../../../models/ui/case-access.model';
import { HelpService } from '../../../services/help.service';
import { CasesService } from '../../../services/cases.service';
import { CaseAccessManager } from '../../../managers/case-access.manager';
import { NotificationManager } from '../../../managers/notification.manager';
import { LexbeCase } from '../../../models/case.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CreateCaseDialogComponent } from './create-case-dialog/create-case-dialog.component';

@Component({
    selector: 'lex-manage-case',
    templateUrl: './manage-case.component.html',
    styleUrls: ['./manage-case.component.scss'],
    standalone: false
})
export class ManageCaseComponent implements OnInit {

	public selectedAccount: Account = null;
	public selectedAccountId: string = null;
	public cases$: Observable<CaseAccess[]>;
	public selectedCase: CaseAccess = null;
	public selectedUsers: string[] = [];
	public hoverFieldId: string = null;

	@ViewChild(MatSort) matSort: MatSort;

	constructor(
		private caseAccessManager: CaseAccessManager,
		private casesService: CasesService,
		private helpService: HelpService,
		private activatedRoute: ActivatedRoute,
		private notificationManager: NotificationManager,
		private dialog: MatDialog) {
		this.cases$ = this.caseAccessManager.caseCollection$;
	}

	ngOnInit(): void {
		this.helpService.updateHelpLink('account-menu/manage-cases');
		this.activatedRoute.queryParams.pipe(take(1)).subscribe(
			param => {
				if (param['accountId']) {
					this.selectedAccountId = param['accountId'];
				}
			});
	}

	public sortData(sort: Sort) {
		this.caseAccessManager.sortCaseAccess(sort);
	}

	public selectAccount(): void {
		this.selectedCase = null;
		if (this.matSort)
			this.matSort.sort({ id: null, start: 'desc', disableClear: false });

		if (this.selectedAccount) {
			this.caseAccessManager.getCasesAccess(this.selectedAccount.accountId);
		}
	}

	public addCase(): void {
		this.addCaseDialog(this.selectedAccount.accountId).subscribe(() => {
			this.selectAccount();
			this.notificationManager.displaySnackBarMessage({ message: 'Case added to account successfully.' });
		}, err => {
			this.notificationManager.displaySnackBarMessage({ message: `Failed to add case to account- ${err?.error?.error?.message}`, type: 'error' });
		});
	}

	private addCaseDialog(accountId: string): Observable<LexbeCase> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = accountId;
		return this.dialog.open(CreateCaseDialogComponent, dialogConfig).afterClosed().pipe(switchMap((lexbeCase: LexbeCase) => {
			if (lexbeCase) {
				return this.caseAccessManager.createCase(lexbeCase);
			}
			return EMPTY;
		}));
	}

	public removeCase(event: any, caseId: string): void {
		event.stopPropagation();
		event.preventDefault();
		this.cases$.pipe(take(1)).subscribe((cases) => {
			if (cases.length == 1) {
				this.notificationManager.displaySnackBarMessage({
					message: `You cannot delete the last remaining case associated with the account. 
					At least one active case must remain to ensure account integrity. 
					Please contact Professional Service for support.`, type: 'error', ttl: 10000
				});
			}
			else {
				this.casesService.deleteCaseDialog(caseId, () => this.selectAccount());
			}
		})
	}

	public getCaseUserList(lexbeCase: CaseAccess): string[] {
		return lexbeCase.accessibleUsers.map(x => x.userName);
	}

	public clearSelectedCase(updateUserAccess: boolean): void {
		this.selectedCase = null;
		this.selectAccount();
		if (updateUserAccess) {
			this.notificationManager.displaySnackBarMessage({ message: 'User access for case updated successfully.' });
		}
	}
}
